/**
 * Client for the RN session bootstrap endpoint.
 *
 * The app calls this on launch and on every return to the foreground. It is the
 * only channel through which the server can tell a running app that the account
 * ended or that the app-lock policy changed — everything else the client knows
 * about the nurse was cached at login and never refreshed.
 */
import { request } from "@/lib/api";

export type AppLockMethod = "pin" | "biometric";

export interface AppLockPolicy {
    /**
     * Whether the feature is live yet. While false the app shows no lock and no
     * setup prompt — the rollout is held until the store build carrying
     * biometrics has spread, so nurses meet the full choice at once.
     */
    enabled: boolean;
    enrolled: boolean;
    method: AppLockMethod | null;
    enrolledAt: string | null;
    /** ISO instant after which setup can no longer be skipped. */
    requiredAt: string;
    /** Server-computed: `requiredAt` has passed. */
    required: boolean;
    /** Bumped by an admin reset; compared against the value this device stored. */
    resetAt: string | null;
    inactivityTimeoutSeconds: number;
}

export interface RnSession {
    account: {
        employmentStatus: string;
        blocked: boolean;
        blockedReason: "terminated" | "deactivated" | null;
    };
    /** Null when the account is blocked — there is nothing to unlock into. */
    appLock: AppLockPolicy | null;
}

export function fetchRnSession(): Promise<RnSession> {
    return request<RnSession>("/rn/me/session");
}

/** Reports completed setup. Called only after the secret is in the OS store. */
export function reportAppLockEnrollment(method: AppLockMethod): Promise<{ data: AppLockPolicy }> {
    return request<{ data: AppLockPolicy }>("/rn/me/app-lock", {
        method: "POST",
        body: JSON.stringify({ method })
    });
}
